/**
 * Reusable form for defining a MarketFit situation (assets, needs, inputs, outputs, actors).
 * Used by SituationDefinitionDrawer and MarketFitSituationPage.
 */
import React from 'react';
import { Button, Input, Select, Table, Spin, Space, Typography, Radio, Segmented } from 'antd';
import { PlusOutlined, DeleteOutlined, UserOutlined } from '@ant-design/icons';
import {
  type SituationDetails,
  type SituationAssetItem,
  type SituationNeedItem,
  type SituationInputItem,
  type SituationOutputItem,
  type SituationSchemaV2,
  type SituationValueOutput,
  MARKETFIT_CATEGORIES,
  SITUATION_VALUE_OUTPUT_TYPES,
  generateId,
} from '../../types/marketFitLocalBridge';
import type { MarketFitActor } from '../../services/localBridgeSessionApi';

const categoryOptions = MARKETFIT_CATEGORIES.map((c) => ({ value: c, label: c }));

export interface SituationDefinitionFormProps {
  data: SituationDetails;
  dataV2: SituationSchemaV2;
  schemaMode: 'legacy' | 'v2';
  onDataChange: (data: SituationDetails) => void;
  onDataV2Change: (data: SituationSchemaV2) => void;
  onSchemaModeChange: (mode: 'legacy' | 'v2') => void;
  onSave: () => void;
  loading?: boolean;
  saving?: boolean;
  actors: MarketFitActor[];
  companyId: string;
  situationId: string | null;
  onCreateActor: (name: string) => Promise<void>;
  showFooter?: boolean;
}

export const SituationDefinitionForm: React.FC<SituationDefinitionFormProps> = ({
  data,
  dataV2,
  schemaMode,
  onDataChange,
  onDataV2Change,
  onSchemaModeChange,
  onSave,
  loading = false,
  saving = false,
  actors,
  situationId,
  onCreateActor,
  showFooter = true,
}) => {
  const [newActorName, setNewActorName] = React.useState('');
  const [creatingActor, setCreatingActor] = React.useState(false);

  const updateAssets = (updater: (prev: SituationAssetItem[]) => SituationAssetItem[]) =>
    onDataChange({ ...data, assets: updater(data.assets) });
  const updateNeeds = (updater: (prev: SituationNeedItem[]) => SituationNeedItem[]) =>
    onDataChange({ ...data, needs: updater(data.needs) });
  const updateInputs = (updater: (prev: SituationInputItem[]) => SituationInputItem[]) =>
    onDataChange({ ...data, inputs: updater(data.inputs) });
  const updateOutputs = (updater: (prev: SituationOutputItem[]) => SituationOutputItem[]) =>
    onDataChange({ ...data, outputs: updater(data.outputs) });

  const addAsset = () =>
    updateAssets((prev) => [...prev, { id: generateId(), name: '', category: 'cost' }]);
  const removeAsset = (id: string) => updateAssets((prev) => prev.filter((a) => a.id !== id));
  const setAsset = (id: string, field: keyof SituationAssetItem, value: string | number | boolean) =>
    updateAssets((prev) => prev.map((a) => (a.id === id ? { ...a, [field]: value } : a)));

  const addNeed = () =>
    updateNeeds((prev) => [...prev, { id: generateId(), name: '', category: 'cost' }]);
  const removeNeed = (id: string) => updateNeeds((prev) => prev.filter((n) => n.id !== id));
  const setNeed = (id: string, field: keyof SituationNeedItem, value: string) =>
    updateNeeds((prev) => prev.map((n) => (n.id === id ? { ...n, [field]: value } : n)));

  const addSituationInput = () =>
    updateInputs((prev) => [...prev, { id: generateId(), name: '', category: 'cost' }]);
  const removeSituationInput = (id: string) => updateInputs((prev) => prev.filter((i) => i.id !== id));
  const setSituationInput = (id: string, field: keyof SituationInputItem, value: string) =>
    updateInputs((prev) => prev.map((i) => (i.id === id ? { ...i, [field]: value } : i)));

  const addOutput = () =>
    updateOutputs((prev) => [...prev, { id: generateId(), name: '', category: 'cost' }]);
  const removeOutput = (id: string) => updateOutputs((prev) => prev.filter((o) => o.id !== id));
  const setOutput = (id: string, field: keyof SituationOutputItem, value: string | number) =>
    updateOutputs((prev) => prev.map((o) => (o.id === id ? { ...o, [field]: value } : o)));

  const setPrimaryActor = (actorId: string) => {
    onDataChange({
      ...data,
      actors: data.actors.map((a) => ({ ...a, is_primary: a.actor_id === actorId })),
    });
  };
  const toggleActor = (actorId: string, add: boolean) => {
    if (add) {
      const already = data.actors.some((a) => a.actor_id === actorId);
      if (already) return;
      const hasPrimary = data.actors.some((a) => a.is_primary);
      onDataChange({
        ...data,
        actors: [...data.actors, { actor_id: actorId, is_primary: !hasPrimary }],
      });
    } else {
      onDataChange({
        ...data,
        actors: data.actors.filter((a) => a.actor_id !== actorId),
      });
    }
  };

  const addValueOutput = () =>
    onDataV2Change({
      ...dataV2,
      value_outputs: [...dataV2.value_outputs, { output_type: 'revenue', unit: '', value_rate: 0, variability: 0, beneficiary: '' }],
    });
  const removeValueOutput = (index: number) =>
    onDataV2Change({
      ...dataV2,
      value_outputs: dataV2.value_outputs.filter((_, i) => i !== index),
    });
  const setValueOutput = (index: number, field: keyof SituationValueOutput, value: string | number) =>
    onDataV2Change({
      ...dataV2,
      value_outputs: dataV2.value_outputs.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    });

  const actorIdsInSituation = new Set(data.actors.map((a) => a.actor_id));
  const primaryId = data.actors.find((a) => a.is_primary)?.actor_id;

  const handleCreateActor = async () => {
    const name = newActorName?.trim();
    if (!name) return;
    setCreatingActor(true);
    try {
      await onCreateActor(name);
      setNewActorName('');
    } finally {
      setCreatingActor(false);
    }
  };

  const content = (
    <Spin spinning={loading}>
      {!situationId ? (
        <Typography.Text type="secondary">Select a situation to define.</Typography.Text>
      ) : (
        <>
          <div style={{ marginBottom: 16 }}>
            <Segmented
              options={[
                { value: 'legacy', label: 'Legacy (assets, needs, actors)' },
                { value: 'v2', label: 'Core schema v2 (value outputs)' },
              ]}
              value={schemaMode}
              onChange={(v) => onSchemaModeChange(v as 'legacy' | 'v2')}
            />
          </div>
          {schemaMode === 'v2' ? (
            <>
              <Typography.Title level={5}>Value outputs (core schema v2)</Typography.Title>
              <Table
                size="small"
                pagination={false}
                dataSource={dataV2.value_outputs.map((row, i) => ({ ...row, _index: i }))}
                rowKey="_index"
                columns={[
                  {
                    title: 'Type',
                    width: 140,
                    render: (_, r) => (
                      <Select
                        value={r.output_type}
                        onChange={(v) => setValueOutput(r._index, 'output_type', v)}
                        options={SITUATION_VALUE_OUTPUT_TYPES.map((t) => ({ value: t, label: t }))}
                        style={{ width: '100%' }}
                      />
                    ),
                  },
                  {
                    title: 'Unit',
                    width: 80,
                    render: (_, r) => (
                      <Input
                        value={r.unit}
                        onChange={(e) => setValueOutput(r._index, 'unit', e.target.value)}
                        placeholder="Unit"
                      />
                    ),
                  },
                  {
                    title: 'Value rate',
                    width: 90,
                    render: (_, r) => (
                      <Input
                        type="number"
                        value={r.value_rate}
                        onChange={(e) => setValueOutput(r._index, 'value_rate', Number(e.target.value) || 0)}
                      />
                    ),
                  },
                  {
                    title: 'Variability',
                    width: 80,
                    render: (_, r) => (
                      <Input
                        type="number"
                        value={r.variability}
                        onChange={(e) => setValueOutput(r._index, 'variability', Number(e.target.value) || 0)}
                      />
                    ),
                  },
                  {
                    title: 'Beneficiary',
                    render: (_, r) => (
                      <Input
                        value={r.beneficiary}
                        onChange={(e) => setValueOutput(r._index, 'beneficiary', e.target.value)}
                        placeholder="Beneficiary"
                      />
                    ),
                  },
                  {
                    width: 50,
                    render: (_, r) => (
                      <Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeValueOutput(r._index)} />
                    ),
                  },
                ]}
              />
              <Button type="dashed" icon={<PlusOutlined />} onClick={addValueOutput} style={{ marginTop: 8 }}>
                Add value output
              </Button>
            </>
          ) : (
            <>
              <Typography.Title level={5}>Assets</Typography.Title>
              <Table
                size="small"
                pagination={false}
                dataSource={data.assets}
                rowKey="id"
                columns={[
                  {
                    title: 'Name',
                    render: (_, r) => (
                      <Input
                        placeholder="Asset name"
                        value={r.name}
                        onChange={(e) => setAsset(r.id, 'name', e.target.value)}
                      />
                    ),
                  },
                  {
                    title: 'Category',
                    width: 120,
                    render: (_, r) => (
                      <Select
                        value={r.category}
                        onChange={(v) => setAsset(r.id, 'category', v)}
                        options={categoryOptions}
                        style={{ width: '100%' }}
                      />
                    ),
                  },
                  {
                    width: 50,
                    render: (_, r) => (
                      <Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeAsset(r.id)} />
                    ),
                  },
                ]}
              />
              <Button type="dashed" icon={<PlusOutlined />} onClick={addAsset} style={{ marginTop: 8 }}>
                Add asset
              </Button>

              <Typography.Title level={5} style={{ marginTop: 20 }}>
                Needs
              </Typography.Title>
              <Table
                size="small"
                pagination={false}
                dataSource={data.needs}
                rowKey="id"
                columns={[
                  {
                    title: 'Name',
                    render: (_, r) => (
                      <Input
                        placeholder="Need name"
                        value={r.name}
                        onChange={(e) => setNeed(r.id, 'name', e.target.value)}
                      />
                    ),
                  },
                  {
                    title: 'Category',
                    width: 120,
                    render: (_, r) => (
                      <Select
                        value={r.category}
                        onChange={(v) => setNeed(r.id, 'category', v)}
                        options={categoryOptions}
                        style={{ width: '100%' }}
                      />
                    ),
                  },
                  {
                    width: 50,
                    render: (_, r) => (
                      <Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeNeed(r.id)} />
                    ),
                  },
                ]}
              />
              <Button type="dashed" icon={<PlusOutlined />} onClick={addNeed} style={{ marginTop: 8 }}>
                Add need
              </Button>

              <Typography.Title level={5} style={{ marginTop: 20 }}>
                Inputs
              </Typography.Title>
              <Table
                size="small"
                pagination={false}
                dataSource={data.inputs}
                rowKey="id"
                columns={[
                  {
                    title: 'Name',
                    render: (_, r) => (
                      <Input
                        placeholder="Input name"
                        value={r.name}
                        onChange={(e) => setSituationInput(r.id, 'name', e.target.value)}
                      />
                    ),
                  },
                  {
                    title: 'Category',
                    width: 120,
                    render: (_, r) => (
                      <Select
                        value={r.category}
                        onChange={(v) => setSituationInput(r.id, 'category', v)}
                        options={categoryOptions}
                        style={{ width: '100%' }}
                      />
                    ),
                  },
                  {
                    width: 50,
                    render: (_, r) => (
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => removeSituationInput(r.id)}
                      />
                    ),
                  },
                ]}
              />
              <Button type="dashed" icon={<PlusOutlined />} onClick={addSituationInput} style={{ marginTop: 8 }}>
                Add input
              </Button>

              <Typography.Title level={5} style={{ marginTop: 20 }}>
                Outputs
              </Typography.Title>
              <Table
                size="small"
                pagination={false}
                dataSource={data.outputs}
                rowKey="id"
                columns={[
                  {
                    title: 'Name',
                    render: (_, r) => (
                      <Input
                        placeholder="Output name"
                        value={r.name}
                        onChange={(e) => setOutput(r.id, 'name', e.target.value)}
                      />
                    ),
                  },
                  {
                    title: 'Category',
                    width: 120,
                    render: (_, r) => (
                      <Select
                        value={r.category}
                        onChange={(v) => setOutput(r.id, 'category', v)}
                        options={categoryOptions}
                        style={{ width: '100%' }}
                      />
                    ),
                  },
                  {
                    width: 50,
                    render: (_, r) => (
                      <Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeOutput(r.id)} />
                    ),
                  },
                ]}
              />
              <Button type="dashed" icon={<PlusOutlined />} onClick={addOutput} style={{ marginTop: 8 }}>
                Add output
              </Button>

              <Typography.Title level={5} style={{ marginTop: 20 }}>
                <UserOutlined /> Actors (at least one; designate primary)
              </Typography.Title>
              <div style={{ marginBottom: 8 }}>
                {actors.length === 0 ? (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Typography.Text type="secondary">
                      No actors yet. Create one for this situation:
                    </Typography.Text>
                    <Space.Compact>
                      <Input
                        placeholder="Actor name"
                        value={newActorName}
                        onChange={(e) => setNewActorName(e.target.value)}
                        onPressEnter={handleCreateActor}
                      />
                      <Button type="primary" loading={creatingActor} onClick={handleCreateActor}>
                        Create actor
                      </Button>
                    </Space.Compact>
                  </Space>
                ) : (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {actors.map((a) => {
                      const inSituation = actorIdsInSituation.has(a.id);
                      const isPrimary = primaryId === a.id;
                      return (
                        <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Button
                            type={inSituation ? 'primary' : 'default'}
                            size="small"
                            onClick={() => toggleActor(a.id, !inSituation)}
                          >
                            {inSituation ? 'In situation' : 'Add'}
                          </Button>
                          <span>{a.actor_name || a.actor_role || a.id.slice(0, 8)}</span>
                          {inSituation && (
                            <Radio checked={isPrimary} onChange={() => setPrimaryActor(a.id)}>
                              Primary
                            </Radio>
                          )}
                        </div>
                      );
                    })}
                  </Space>
                )}
              </div>
            </>
          )}
        </>
      )}
    </Spin>
  );

  return (
    <>
      {content}
      {showFooter && (
        <div style={{ marginTop: 16 }}>
          <Space>
            <Button type="primary" loading={saving} onClick={onSave}>
              Save
            </Button>
          </Space>
        </div>
      )}
    </>
  );
};
